<template>
  <div :class="componentClass" @click="navigate">
    <div class="description-container">
      <div class="spacer" />
      <h6>{{ step.title }}</h6>
      <p>{{ step.text }}</p>
      <div class="spacer" />
    </div>
    <div class="line-container">
      <svg class="progress-step-line-svg">
        <defs>
          <linearGradient id="ps-active-to-completed" y1="0" y2="1" x1="0" x2="0">
            <stop offset="5%" stop-color="#008BAE" />
            <stop offset="95%" stop-color="#00b981" />
          </linearGradient>
          <linearGradient id="ps-completed-to-active" y1="0" y2="1" x1="0" x2="0">
            <stop offset="5%" stop-color="#00b981" />
            <stop offset="95%" stop-color="#008BAE" />
          </linearGradient>

          <linearGradient id="ps-neutral-to-completed" y1="0" y2="1" x1="0" x2="0">
            <stop offset="5%" stop-color="#E3E7EE" />
            <stop offset="95%" stop-color="#00b981" />
          </linearGradient>
          <linearGradient id="ps-neutral-to-active" y1="0" y2="1" x1="0" x2="0">
            <stop offset="5%" stop-color="#E3E7EE" />
            <stop offset="95%" stop-color="#008BAE" />
          </linearGradient>
          <linearGradient id="ps-completed-to-neutral" y1="0" y2="1" x1="0" x2="0">
            <stop offset="5%" stop-color="#00b981" />
            <stop offset="95%" stop-color="#E3E7EE" />
          </linearGradient>
          <linearGradient id="ps-active-to-neutral" y1="0" y2="1" x1="0" x2="0">
            <stop offset="5%" stop-color="#008BAE" />
            <stop offset="95%" stop-color="#E3E7EE" />
          </linearGradient>
          <linearGradient id="ps-completed-to-completed" y1="0" y2="1" x1="0" x2="0">
            <stop offset="5%" stop-color="#00b981" />
            <stop offset="95%" stop-color="#00b981" />
          </linearGradient>
          <linearGradient id="ps-active-to-active" y1="0" y2="1" x1="0" x2="0">
            <stop offset="5%" stop-color="#008BAE" />
            <stop offset="95%" stop-color="#008BAE" />
          </linearGradient>
          <linearGradient id="ps-neutral-to-neutral" y1="0" y2="1" x1="0" x2="0">
            <stop offset="5%" stop-color="#E3E7EE" />
            <stop offset="95%" stop-color="#E3E7EE" />
          </linearGradient>
        </defs>
        <line v-if="!initial" y1="-100%" y2="42" x1="34" x2="34.0001" :class="lineClass" />
        <line v-if="!final" y1="42" y2="100%" x1="34" x2="34.0001" :class="nextLineClass" />
        <g>
          <circle cx="34" cy="42" r="6" :class="circleClass" />
        </g>
      </svg>
    </div>
  </div>
</template>

<script>
export default {
  name: "ProStep",
  props: {
    step: Object,
    initial: Boolean,
    final: Boolean,

    isNext: Boolean,
    isNextActive: Boolean,
    isNextCompleted: Boolean
  },
  methods: {
    navigate() {
      this.$store.dispatch(this.step.navigateAction, this.step.view);
    }
  },
  computed: {
    componentClass() {
      let result = "progress-step";
      if (this.step.completed) result += " completed";
      if (this.step.active) result += " active";
      return result;
    },

    nextLineClass() {
      let result = "line";

      if (this.step.completed) result += " completed";
      else if (this.step.active) result += " active";
      else result += " neutral";

      if (this.isNext) {
        if (this.isNextCompleted) result += "-to-completed";
        else if (this.isNextActive) result += "-to-active";
        else result += "-to-neutral";
      }

      return result;
    },

    lineClass() {
      let result = "line";
      if (this.final) result += " final";
      if (this.initial) result += " initial";

      if (this.step.active) result += " active";
      if (this.step.completed) result += " completed";
      return result;
    },

    circleClass() {
      let result = "circle";
      if (this.final) result += " final";
      if (this.initial) result += " initial";

      if (this.step.active) result += " active";
      if (this.step.completed) result += " completed";
      return result;
    }
  }
};
</script>

<style lang='scss' scoped>
@import "../../utils/assets/stylesheets/variables.scss";

.progress-step {
  position: relative;
  display: flex;
  width: 100%;

  overflow: visible;
  border-radius: $border-radius;

  &.active {
    background-color: $gray-lighten-2;

    .description-container {
      h6 {
        color: $primary;
        font-weight: 600;
      }
      p {
        color: $primary-lighten-1;
      }
    }
  }

  .line-container {
    width: 90px;
  }

  .description-container {
    width: 100%;
    padding-left: 35px;
    h6 {
      color: $primary-lighten-2;
      font-weight: 600;
    }
    p {
      color: $primary-lighten-2;
    }
  }
}

.spacer {
  display: block;
  height: 30px;
}

.progress-step-line-svg {
  position: absolute;
  height: 100%;

  .line {
    stroke-width: 4;
    stroke: $gray;

    &.neutral {
      stroke: $gray;
    }
    &.active {
      stroke: $secondary;
    }
    &.completed {
      stroke: $positive;
    }

    &.active-to-completed {
      stroke: url(#ps-active-to-completed);
    }
    &.completed-to-active {
      stroke: url(#ps-completed-to-active);
    }
    &.neutral-to-completed {
      stroke: url(#ps-neutral-to-completed);
    }
    &.neutral-to-active {
      stroke: url(#ps-neutral-to-neutral);
    }
    &.completed-to-neutral {
      stroke: url(#ps-completed-to-completed);
    }
    &.active-to-neutral {
      stroke: url(#ps-active-to-active);
    }
    &.completed-to-completed {
      stroke: url(#ps-completed-to-completed);
    }
  }

  .circle {
    fill: $gray;
    stroke: white;
    stroke-width: 4;

    &.final,
    &.initial {
      stroke: $gray;
      fill: white;
    }

    &.active {
      stroke: $gray-lighten-2;
      fill: $secondary;

      &.final,
      &.initial {
        stroke: $secondary;
        fill: white;
      }
    }

    &.completed {
      stroke: white;
      fill: $positive;

      &.final,
      &.initial {
        stroke: $positive;
        fill: white;
      }
    }
  }
}
</style>
